import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Plus, Save, Search, Shield, UserCog, UserX } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { useAuth } from '@/hooks/useAuth';
import {
  UsuarioApi,
  useAtualizarUsuario,
  useCriarUsuario,
  useDesativarUsuario,
  useUsuarios,
} from '@/hooks/useUsuarios';

const defaultForm = {
  name: '',
  email: '',
  password: '',
  role: 'user' as 'admin' | 'user' | 'consultor',
};

const parseErrorMessage = (error: unknown) => {
  if (typeof error === 'object' && error !== null) {
    if ('message' in error && typeof (error as { message?: unknown }).message === 'string') {
      return (error as { message: string }).message;
    }

    const maybeApi = error as {
      error?: {
        message?: string;
        details?: Array<{ msg?: string }>;
      };
    };

    if (maybeApi.error?.details && maybeApi.error.details.length > 0) {
      const firstMessage = maybeApi.error.details[0]?.msg;
      if (firstMessage) {
        return firstMessage;
      }
    }

    if (maybeApi.error?.message) {
      return maybeApi.error.message;
    }
  }

  return 'Erro ao salvar usuário';
};

function Usuarios() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState<UsuarioApi | null>(null);
  const [form, setForm] = useState(defaultForm);
  const [message, setMessage] = useState<string | null>(null);

  const { data, isLoading } = useUsuarios({
    search: search || undefined,
    page: 1,
    limit: 50,
  });
  const criarUsuario = useCriarUsuario();
  const atualizarUsuario = useAtualizarUsuario();
  const desativarUsuario = useDesativarUsuario();

  const usuarios = data?.users || [];

  const isSaving = useMemo(
    () => criarUsuario.isPending || atualizarUsuario.isPending,
    [criarUsuario.isPending, atualizarUsuario.isPending]
  );

  if (user?.email?.toLowerCase() !== 'admin@alpendre.infra') {
    return <Navigate to="/" replace />;
  }

  const resetForm = () => {
    setEditingUser(null);
    setForm(defaultForm);
  };

  const fillFormForEdit = (target: UsuarioApi) => {
    setEditingUser(target);
    setForm({
      name: target.name,
      email: target.email,
      password: '',
      role: target.role,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);

    try {
      if (editingUser) {
        await atualizarUsuario.mutateAsync({
          id: editingUser.id,
          name: form.name,
          email: form.email,
          role: form.role,
          ...(form.password ? { password: form.password } : {}),
        });
        setMessage('Usuário atualizado com sucesso.');
      } else {
        await criarUsuario.mutateAsync({
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
        });
        setMessage('Usuário criado com sucesso.');
      }

      resetForm();
    } catch (error) {
      setMessage(parseErrorMessage(error));
    }
  };

  const handleDisable = async (target: UsuarioApi) => {
    const confirmed = window.confirm(`Desativar ${target.name}?`);
    if (!confirmed) {
      return;
    }

    try {
      await desativarUsuario.mutateAsync(target.id);
      if (editingUser?.id === target.id) {
        resetForm();
      }
      setMessage('Usuário desativado com sucesso.');
    } catch (error) {
      setMessage(parseErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ds-text-primary">Usuários</h1>
          <p className="text-ds-text-secondary mt-1">
            Controle de acesso do sistema com permissões por perfil.
          </p>
        </div>
      </div>

      {message && (
        <div className="p-3 rounded-lg bg-ds-info/10 border border-ds-info/30 text-ds-text-primary text-sm">
          {message}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {editingUser ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {editingUser ? 'Editar usuário' : 'Novo usuário'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-ds-text-secondary block mb-2">Nome</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Nome completo"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-ds-text-secondary block mb-2">Email</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="usuario@empresa.com"
                  required
                />
              </div>

              <div>
                <label className="text-sm text-ds-text-secondary block mb-2">
                  {editingUser ? 'Nova senha (opcional)' : 'Senha'}
                </label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                  placeholder="10+ caracteres, com maiúscula, minúscula e número"
                  required={!editingUser}
                />
              </div>

              <div>
                <label className="text-sm text-ds-text-secondary block mb-2">Perfil</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value as 'admin' | 'user' | 'consultor' }))}
                  className="w-full bg-ds-surface2 border border-ds-border rounded-lg px-3 py-2 text-sm text-ds-text-primary"
                >
                  <option value="user">Usuário</option>
                  <option value="consultor">Consultor</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1" isLoading={isSaving}>
                  {editingUser ? 'Salvar alterações' : 'Criar usuário'}
                </Button>
                {editingUser && (
                  <Button type="button" variant="secondary" onClick={resetForm}>
                    Cancelar
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-3">
              <span>Lista de usuários</span>
              <div className="relative w-full max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ds-text-secondary" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nome ou email"
                  className="pl-10"
                />
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ds-success" />
              </div>
            ) : usuarios.length === 0 ? (
              <div className="text-center py-12 text-sm text-ds-text-secondary">
                Nenhum usuário encontrado.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Último acesso</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usuarios.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium text-ds-text-primary">{item.name}</div>
                          <div className="text-xs text-ds-text-secondary">{item.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.role === 'admin' ? 'success' : 'default'}>
                          {item.role === 'admin' ? (
                            <span className="inline-flex items-center gap-1">
                              <Shield className="h-3 w-3" /> Admin
                            </span>
                          ) : item.role === 'consultor' ? (
                            <span className="inline-flex items-center gap-1">
                              <UserCog className="h-3 w-3" /> Consultor
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <UserCog className="h-3 w-3" /> Usuário
                            </span>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.isActive ? 'success' : 'danger'}>
                          {item.isActive ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-ds-text-secondary">
                        {item.lastLoginAt
                          ? new Date(item.lastLoginAt).toLocaleString('pt-BR')
                          : 'Nunca'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => fillFormForEdit(item)}
                          >
                            Editar
                          </Button>
                          {item.isActive && (
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => {
                                void handleDisable(item);
                              }}
                              disabled={item.id === Number(user?.id) || desativarUsuario.isPending}
                            >
                              <UserX className="h-4 w-4 mr-1" />
                              Desativar
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export { Usuarios };
